import { GroupPeopleType, ImageFrameRole } from '@lite-workspace/dto-fotoclass-templates';
import { getSpreadGroups, type SpreadData } from '@lite-workspace/photobook-model';
import { SpreadSlotTypeEnum, SpreadTypeEnum } from '../spread.dto';

/** Контентный разворот (портрет / auto-layout) — виньетки и JPEG имеют свои категории синхронизации.
 * Разворот с размеченными рамками — тоже контентный: своего типа у него нет, синхронизируется позиционно. */
export function isLayoutSyncableSpread(spread: { spread_type: number; slot_type?: string | null }): boolean {
  return spread.spread_type === SpreadTypeEnum.FREE && spread.slot_type !== SpreadSlotTypeEnum.UPLOADED_PHOTO_SPREAD;
}

/** Готовый JPEG-разворот (вгруженная картинка) — переносится целиком в другие альбомы как фиксированный ассет. */
export function isJpegSyncableSpread(spread: { spread_type: number; slot_type?: string | null }): boolean {
  return spread.spread_type === SpreadTypeEnum.FREE && spread.slot_type === SpreadSlotTypeEnum.UPLOADED_PHOTO_SPREAD;
}

/** Виньеточный разворот (преподаватели/ученики) — переносится как обычный, раскладка/текст по выбору. */
export function isVignetteSyncableSpread(spread: { spread_type: number }): boolean {
  return spread.spread_type === SpreadTypeEnum.VIGNETTE;
}

/** Минимум структуры разворота, который нужен обходу рамок: слой рисуемых объектов читается по `type`. */
interface FrameHost {
  readonly type?: string;
  readonly frameRole?: string | null;
  readonly drawableObjects?: readonly FrameHost[];
}

function someFrameIsCommonPhoto(drawables: readonly FrameHost[] | undefined): boolean {
  return (drawables ?? []).some(drawable =>
    drawable.type === 'group'
      ? someFrameIsCommonPhoto(drawable.drawableObjects)
      : drawable.frameRole === ImageFrameRole.COMMON_PHOTO,
  );
}

/**
 * На развороте есть рамка «Общее фото». ЕДИНСТВЕННЫЙ признак общего кадра класса: своего типа разворота
 * у него больше нет, и фронт, и бэк спрашивают отсюда.
 */
export function hasCommonPhotoFrame(spread: {
  collages?: readonly { readonly drawableObjects?: readonly FrameHost[] }[];
}): boolean {
  return (spread.collages ?? []).some(collage => someFrameIsCommonPhoto(collage.drawableObjects));
}

/**
 * Точка вставки добивки: сразу за последним разворотом без рамки «Общее фото» — общее фото класса остаётся
 * хвостом книги. Своего типа у общего разворота нет: общий кадр держится ролью рамки (`hasCommonPhotoFrame`).
 */
export function deficitFillInsertIndex(spreads: readonly Parameters<typeof hasCommonPhotoFrame>[0][]): number {
  for (let i = spreads.length - 1; i >= 0; i--) {
    if (!hasCommonPhotoFrame(spreads[i])) return i + 1;
  }

  return 0;
}

/** Логический разряд синхронизации — матчинг и подсчёт «недостающих» идут по порядку ВНУТРИ разряда. */
export type SpreadSyncBucket = 'content' | 'jpeg' | 'vignette';

const VIGNETTE_SLOT_TYPES: ReadonlySet<SpreadSlotTypeEnum> = new Set([
  SpreadSlotTypeEnum.STUDENT_VIGNETTE,
  SpreadSlotTypeEnum.TEACHER_VIGNETTE,
  SpreadSlotTypeEnum.COMBINED_VIGNETTE,
  SpreadSlotTypeEnum.ME_AND_TEACHERS_VIGNETTE,
]);

export function isVignetteSlotType(slotType: SpreadSlotTypeEnum | string | null | undefined): boolean {
  return !!slotType && VIGNETTE_SLOT_TYPES.has(slotType as SpreadSlotTypeEnum);
}

/** Виньеточные слоты, на которых могут сидеть педагоги: «Только ученики» их не несёт. */
const TEACHER_VIGNETTE_SLOT_TYPES: ReadonlySet<SpreadSlotTypeEnum> = new Set([
  SpreadSlotTypeEnum.TEACHER_VIGNETTE,
  SpreadSlotTypeEnum.COMBINED_VIGNETTE,
  SpreadSlotTypeEnum.ME_AND_TEACHERS_VIGNETTE,
]);

/**
 * Педагог посажен хотя бы на одну виньетку книги. Решает посаженная рамка, не слот: совмещённая виньетка,
 * с которой педагогов убрали, их не показывает.
 */
export function vignetteSeatsTeacher(
  spreads: readonly (Pick<SpreadData, 'collages'> & { slot_type?: string | null })[],
): boolean {
  return spreads.some(
    spread =>
      TEACHER_VIGNETTE_SLOT_TYPES.has(spread.slot_type as SpreadSlotTypeEnum) &&
      getSpreadGroups(spread).some(
        group =>
          !!group.params.personId &&
          (group.params.peopleType === GroupPeopleType.TEACHER || group.params.peopleType === GroupPeopleType.CLASS_TEACHER),
      ),
  );
}

/** Разряд разворота по `slot_type` (доступен и в settings.spreads, и в model.spreads). */
export function spreadSyncBucket(slotType: SpreadSlotTypeEnum | string | null | undefined): SpreadSyncBucket {
  if (slotType === SpreadSlotTypeEnum.UPLOADED_PHOTO_SPREAD) return 'jpeg';
  if (slotType && VIGNETTE_SLOT_TYPES.has(slotType as SpreadSlotTypeEnum)) return 'vignette';
  return 'content';
}

/** Кол-во разворотов каждого логического разряда по списку `slot_type` (лёгкий список альбомов). */
export function countSpreadsByBucket(
  slotTypes: ReadonlyArray<SpreadSlotTypeEnum | string | null | undefined>,
): Map<SpreadSyncBucket, number> {
  const counts = new Map<SpreadSyncBucket, number>();
  for (const slotType of slotTypes) {
    const bucket = spreadSyncBucket(slotType);
    counts.set(bucket, (counts.get(bucket) ?? 0) + 1);
  }

  return counts;
}
